import { Fragment, useEffect, useState, type ReactNode } from "react";
import { Popover, Dialog, Flex, Text, VisuallyHidden } from "@radix-ui/themes";
import { Home, MoreHorizontal, LogOut } from "lucide-react";
import { useLocation } from "react-router-dom";
import type { PageKey, AppNavigationTarget } from "../../appNavTypes";
import { GAME_NAV_TABS, battleTabFor, type GameNavTab } from "./gameNavConfig";
import { GameProfileCard } from "./GameProfileCard";
import { useAttentionPets } from "../../hooks/useAttentionPets";
import { useDailyRivalAvailable } from "../../hooks/useDailyRival";
import { useWalletConnection } from "../../auth/useWalletConnection";
import { useUnifiedLogout } from "../../auth/useUnifiedLogout";
import { AudioMuteButton } from "./AudioMuteButton";
import { useT } from "../../i18n/i18nContext";

interface GameMobileBarProps {
  activePage: PageKey;
  onNavigate: (target: AppNavigationTarget) => void;
  actingAddress: string | null;
  accountSlot?: ReactNode;
}

/** Keys of the tabs shown as quick items in the mobile bottom bar (besides Home + More). */
const MOBILE_QUICK_KEYS = ["play", "campaign", "battle"] as const;

/**
 * Mobile bottom navigation. Phones only have room for a few icons, so the bar shows Home,
 * Play, Campaign, Battle (with its Training/Solo/Squad selector — a single tap into Training
 * for a visitor with no identity, see battleTabFor) and a "More" (⋯) button that opens a side
 * drawer with the complete set of entries plus the profile and account controls.
 */
export function GameMobileBar({ activePage, onNavigate, actingAddress, accountSlot }: GameMobileBarProps) {
  const { count: attentionCount } = useAttentionPets(actingAddress);
  const dailyRivalAvailable = useDailyRivalAvailable(actingAddress);
  const { isConnected } = useWalletConnection();
  const logout = useUnifiedLogout();
  const [moreOpen, setMoreOpen] = useState(false);
  const t = useT();
  const { pathname } = useLocation();

  // Close the drawer on any route change (some controls navigate without an explicit close).
  useEffect(() => { setMoreOpen(false); }, [pathname]);

  // Ranked needs an authenticated identity; a guest or a first-time visitor is sent straight
  // into Training instead of a sign-up gate. Applied to the bar AND the "More" drawer so the
  // two never disagree about where Battle goes. See battleTabFor.
  const navTabs = GAME_NAV_TABS.filter((tab) => !tab.hiddenFromMenu).map((tab) => battleTabFor(tab, isConnected));

  const quickTabs = MOBILE_QUICK_KEYS
    .map((key) => navTabs.find((tab) => tab.key === key))
    .filter((tab): tab is GameNavTab => !!tab);

  const renderQuickTab = (tab: GameNavTab) => {
    const isActive = tab.activePages.includes(activePage);
    const showDot =
      (tab.notify === "pet-care" && attentionCount > 0) ||
      (tab.notify === "daily-rival" && dailyRivalAvailable);
    const inner = (
      <>
        <span className="game-nav__icon" aria-hidden="true">
          <tab.Icon size={22} strokeWidth={2} />
          {showDot && <span className="game-nav__dot" />}
        </span>
        <span className="game-nav__label">{t(tab.label)}</span>
      </>
    );

    if (tab.submenu) {
      return (
        <Popover.Root key={tab.key}>
          <Popover.Trigger>
            <button type="button" className={`game-mobile-bar__tab${isActive ? " game-mobile-bar__tab--active" : ""}`} aria-label={t(tab.ariaLabel)}>
              {inner}
            </button>
          </Popover.Trigger>
          <Popover.Content side="top" align="center" size="1" className="game-popover" style={{ maxWidth: 240 }}>
            <Flex direction="column" gap="1">
              {tab.submenu.map((sub) => (
                <Popover.Close key={sub.key}>
                  <button type="button" className="game-submenu__item" onClick={() => onNavigate(sub.path as AppNavigationTarget)}>
                    <span className="game-submenu__icon" aria-hidden="true"><sub.Icon size={18} strokeWidth={2} /></span>
                    <span className="game-submenu__meta">
                      <Text size="2" weight="bold">{t(sub.label)}</Text>
                      <Text size="1" className="game-submenu__desc">{t(sub.description)}</Text>
                    </span>
                  </button>
                </Popover.Close>
              ))}
            </Flex>
          </Popover.Content>
        </Popover.Root>
      );
    }

    return (
      <button
        key={tab.key}
        type="button"
        className={`game-mobile-bar__tab${isActive ? " game-mobile-bar__tab--active" : ""}`}
        onClick={() => onNavigate(tab.path as AppNavigationTarget)}
        aria-label={showDot ? t("navigation.game.newNotification", { label: t(tab.ariaLabel) }) : t(tab.ariaLabel)}
      >
        {inner}
      </button>
    );
  };

  return (
    <div className="game-mobile-bar" role="navigation" aria-label={t("navigation.game.ariaLabel")}>
      <button type="button" className="game-mobile-bar__tab" onClick={() => onNavigate("/")} aria-label={t("navigation.game.home")}>
        <span className="game-nav__icon" aria-hidden="true"><Home size={22} strokeWidth={2} /></span>
        <span className="game-nav__label">{t("navigation.game.home")}</span>
      </button>

      {quickTabs.map(renderQuickTab)}

      <Dialog.Root open={moreOpen} onOpenChange={setMoreOpen}>
        <Dialog.Trigger>
          <button type="button" className="game-mobile-bar__tab" aria-label={t("navigation.game.more")}>
            <span className="game-nav__icon" aria-hidden="true"><MoreHorizontal size={22} strokeWidth={2} /></span>
            <span className="game-nav__label">{t("navigation.game.more")}</span>
          </button>
        </Dialog.Trigger>
        <Dialog.Content className="game-more-sheet">
          <VisuallyHidden><Dialog.Title>{t("navigation.game.menuTitle")}</Dialog.Title></VisuallyHidden>

          <div className="game-more-sheet__profile">
            <GameProfileCard actingAddress={actingAddress} onNavigate={onNavigate} />
            {accountSlot && <div className="game-more-sheet__account">{accountSlot}</div>}
          </div>

          <nav className="game-more-sheet__list" aria-label={t("navigation.game.allDestinations")}>
            {navTabs.map((tab, index) => {
              // Divide the primary destinations (Battle/Squad and above) from the secondary
              // ones (Collection → Help): a separator sits before the first secondary entry.
              const showSeparator = !!tab.secondary && !navTabs[index - 1]?.secondary;
              const separator = showSeparator ? (
                <div className="game-more-sheet__separator" role="separator" />
              ) : null;

              // Battle expands into its sub-options in the full list.
              if (tab.submenu) {
                return (
                  <Fragment key={tab.key}>
                    {separator}
                    <div className="game-more-sheet__group">
                      <span className="game-more-sheet__group-label">{t(tab.label)}</span>
                      {tab.submenu.map((sub) => (
                        <Dialog.Close key={sub.key}>
                          <button type="button" className="game-more-sheet__row" onClick={() => onNavigate(sub.path as AppNavigationTarget)}>
                            <sub.Icon size={18} strokeWidth={2} />
                            <span>{t(sub.label)}</span>
                          </button>
                        </Dialog.Close>
                      ))}
                    </div>
                  </Fragment>
                );
              }
              return (
                <Fragment key={tab.key}>
                  {separator}
                  <Dialog.Close>
                    <button
                      type="button"
                      className={`game-more-sheet__row${tab.secondary ? " game-more-sheet__row--compact" : ""}`}
                      onClick={() => {
                        if (tab.externalUrl) {
                          window.open(tab.externalUrl, "_blank", "noopener,noreferrer");
                        } else {
                          onNavigate(tab.path as AppNavigationTarget);
                        }
                      }}
                    >
                      <tab.Icon size={18} strokeWidth={2} />
                      <span>{t(tab.label)}</span>
                    </button>
                  </Dialog.Close>
                </Fragment>
              );
            })}

            <div className="game-more-sheet__separator" role="separator" />
            {/* Mute toggle for players who'd rather listen to their own music. Kept
                outside a Dialog.Close so the drawer stays open while they toggle it. */}
            <AudioMuteButton variant="row" />

            {/* Sign-out lives at the very end of the drawer, for authenticated players only. */}
            {isConnected && (
              <Dialog.Close>
                <button
                  type="button"
                  className="game-more-sheet__row game-more-sheet__row--logout"
                  onClick={logout}
                >
                  <LogOut size={18} strokeWidth={2} />
                  <span>{t("navigation.game.logout")}</span>
                </button>
              </Dialog.Close>
            )}
          </nav>
        </Dialog.Content>
      </Dialog.Root>
    </div>
  );
}
